import { useState, useEffect, useCallback } from 'react';
import { fetchMetrics } from '../../server/metricsService';

const computeDates = (meta) => {
  const { dateSelectionMode, rollingPeriod, dates } = meta || {};
  if (dateSelectionMode === 'rollingPeriod' && rollingPeriod) {
    const now = new Date();
    const endDate = now.toISOString().split('.')[0];
    const startObj = new Date(now);
    const { value, unit } = rollingPeriod;
    switch (unit) {
      case 'hours':  startObj.setHours(startObj.getHours() - value); break;
      case 'days':   startObj.setDate(startObj.getDate() - value);   break;
      case 'months': startObj.setMonth(startObj.getMonth() - value); break;
      case 'years':  startObj.setFullYear(startObj.getFullYear() - value); break;
      default: break;
    }
    return { startDate: startObj.toISOString().split('.')[0], endDate };
  }
  return { startDate: dates?.start || null, endDate: dates?.end || null };
};

/**
 * useWidgetData
 *
 * Fetches metric time-series data for a custom widget from the middleware
 * and returns it in ApexCharts-compatible series format.
 *
 * The widget meta contains:
 *   items              — [{ id, name }]  devices/sensors to query
 *   metrics            — ["Noise_average", "Noise_max"]  metric names
 *   dates              — { start, end }  time range (fixed)
 *   dateSelectionMode  — 'fixedDates' | 'rollingPeriod'
 *   rollingPeriod      — { value: number, unit: 'hours'|'days'|'months'|'years' }
 *   period             — "DAILY" | "HOURLY" | "WEEKLY" | "MONTHLY"
 *   visualizationType  — 'aggregated' (average items) | 'separated' (one series per item)
 *
 * @param {object} meta  — the widget's meta object (all fields from the API)
 * @returns {{ series, labels, loading, error }}
 */
const useWidgetData = (meta) => {
  const [series, setSeries] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (silent = false) => {
    const items = meta?.items || [];
    const metrics = meta?.metrics || [];
    const { startDate, endDate } = computeDates(meta);
    const periodicity = meta?.period || 'DAILY';
    const visualizationType = meta?.visualizationType || 'aggregated';

    if (!items.length || !metrics.length || !startDate || !endDate) {
      setSeries([]);
      setLabels([]);
      return;
    }

    if (!silent) setLoading(true);
    setError(null);

    try {
      // Fetch data for all items in parallel
      const results = await Promise.all(
        items.map(async (item) => {
          const itemId = typeof item === 'string' ? item : item.id;
          const itemName = typeof item === 'string' ? item : (item.name || itemId);
          try {
            const data = await fetchMetrics(itemId, periodicity, startDate, endDate);
            return { itemId, itemName, data };
          } catch (err) {
            return { itemId, itemName, data: null };
          }
        })
      );

      // ── Parse the response into ApexCharts series ──
      // Response format: { [metricKey]: { unit: string, data: { [dateStr]: number } } }
      //
      // Multiple items can return different date ranges → we must build a
      // UNIFIED date index and pad missing values with null so every series
      // has exactly the same length (ApexCharts requirement).

      // Step 1: collect the full union of all date keys across all items/metrics
      const allDatesSet = new Set();
      results.forEach(({ data }) => {
        if (!data) return;
        metrics.forEach((metricKey) => {
          const entry = data[metricKey];
          if (entry?.data && typeof entry.data === 'object' && !Array.isArray(entry.data)) {
            Object.keys(entry.data).forEach(d => allDatesSet.add(d));
          }
        });
      });
      const allDates = Array.from(allDatesSet).sort(); // chronological order
      const sharedLabels = allDates.map(d => d.includes('T') ? d.split('T')[0] : d);

      const builtSeries = [];

      if (visualizationType === 'separated') {
        // ── Separated: one series per item per metric ──
        results.forEach(({ itemName, data }) => {
          if (!data) return;
          metrics.forEach((metricKey) => {
            const metricEntry = data[metricKey];
            if (!metricEntry) return;
            const unit = metricEntry.unit || '';
            const valuesMap = metricEntry.data;
            if (!valuesMap || typeof valuesMap !== 'object' || Array.isArray(valuesMap)) return;
            const aligned = allDates.map(d => {
              const v = valuesMap[d];
              return (v === undefined || v === null) ? null : (typeof v === 'number' ? v : parseFloat(v) ?? null);
            });
            builtSeries.push({
              name: `${itemName} - ${metricKey}${unit ? ` (${unit})` : ''}`,
              data: aligned,
            });
          });
        });
      } else {
        // ── Aggregated: average all items per metric per timestamp ──
        metrics.forEach((metricKey) => {
          // Collect values per date across all items
          const combined = {}; // { date: number[] }
          let unit = '';

          results.forEach(({ data }) => {
            if (!data) return;
            const metricEntry = data[metricKey];
            if (!metricEntry) return;
            unit = metricEntry.unit || unit;
            const valuesMap = metricEntry.data;
            if (!valuesMap || typeof valuesMap !== 'object' || Array.isArray(valuesMap)) return;
            Object.entries(valuesMap).forEach(([d, v]) => {
              if (!combined[d]) combined[d] = [];
              const n = typeof v === 'number' ? v : parseFloat(v);
              if (!isNaN(n)) combined[d].push(n);
            });
          });

          const aligned = allDates.map(d => {
            const vals = combined[d];
            if (!vals || vals.length === 0) return null;
            return vals.reduce((a, b) => a + b, 0) / vals.length;
          });

          builtSeries.push({
            name: `${metricKey}${unit ? ` (${unit})` : ''}`,
            data: aligned,
          });
        });
      }

      setSeries(builtSeries);
      setLabels(sharedLabels);
    } catch (err) {
      setError(err?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [meta]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { series, labels, loading, error, refetch: fetchData };
};

export default useWidgetData;
